/* global google, markerClusterer */
import handleErrors from '../utils/_handleErrors';

const urlUtils = require('../utils/_urlUtils');

class oStoreLocator extends HTMLDivElement {
    constructor() {
        super();
        this.storesList = this.querySelector('#stores-list');
        this.storesMap = this.querySelector('#stores-map');
        this.sidebarFilter = this.querySelector('#o-sidebar-storelocator-filters');
        this.slider = this.querySelector('div[is="m-store-locator-slider"]');
        this.toggleViewBtn = this.querySelector('#toggle-view-btn');
        this.storeElemEvents = this.querySelectorAll('[data-store-event]');
        this.storesSearch = this.querySelector('[is="m-stores-search"');
        this.mapCanvas = this.querySelector('#map-canvas');
        this.closestStoreId = this.dataset.closestStore;

        this.filters = [];
        this.stores = [];
        this.markers = [];
        this.storesHidden = [];
        this.markersMap = {};

        this.coordinates = {};
        this.zoomLevel = 11; // Zoom for 78km, used when a city is search

        this.mapView = false;
        this.mapsLoaded = false;
        this.initMap = false;
        this.initCoordinates = false;
        this.fitCloserMarker = false;

        this.map = null;
        this.bound = null;
        this.viewport = null;
        this.markerClusterer = null;
        this.infoWindowOpen = null;

        this.googlePlaces = null;
        this.googleGeocoder = null;

        this.search = this.search.bind(this);
        this.initStoreLocator = this.initStoreLocator.bind(this);
        this.onMapLoaded = this.onMapLoaded.bind(this);
        this.onStoreMapLoaded = this.onStoreMapLoaded.bind(this);
        this.clusterClickHandler = this.clusterClickHandler.bind(this);
        this.triggerStoreEvent = this.triggerStoreEvent.bind(this);
    }

    connectedCallback() {
        if (this.mapCanvas) {
            this.svgMarker = this.mapCanvas.closest('section').dataset.svgMarker;
            this.svgMarkerBrand = this.mapCanvas.closest('section').dataset.svgMarkerBrand;
            this.svgMarkerSearch = this.mapCanvas.closest('section').dataset.svgMarkerSearch;
        }

        if (this.storesList) {
            this.initServices();
            this.initStoreLocator();
            this.addEventListener('storelocator:search', this.search);
            this.addEventListener('storelocator:applyFilters', this.applyFilter);
            this.toggleViewBtn.addEventListener('click', () => { this.toggleView(); });
            this.getAllStores();
        }
        if (Object.hasOwnProperty.call(this.dataset, 'storeDetails') && this.dataset.storeLat && this.dataset.storeLong) {
            this.coordinates = {
                lat: parseFloat(this.dataset.storeLat),
                long: parseFloat(this.dataset.storeLong),
            };
            this.zoomLevel = 17;
            this.openMap();
        }
        this.storeElemEvents.forEach((elem) => {
            elem.addEventListener('click', this.triggerStoreEvent);
        });
    }

    initServices() {
        this.googlePlaces = new google.maps.places.AutocompleteService();
        this.googleGeocoder = new google.maps.Geocoder();
    }

    initStoreLocator() {
        if (!this.storesSearch.value) {
            this.zoomLevel = 3;
            this.coordinates.lat = 33;
            this.coordinates.long = 3;
            return;
        }
        // Get the latitude and longitude of the search by getting the first result of google place for the given input
        this.googlePlaces.getPlacePredictions({ input: this.storesSearch.value, types: ['geocode'] }, (results, status) => {
            if (status === google.maps.places.PlacesServiceStatus.OK && results.length > 0) {
                const isCity = this.storesSearch.cityTypes.includes(results[0].types[0]);
                this.googleGeocoder.geocode({ placeId: results[0].place_id }, (res, stat) => {
                    if (stat === 'OK') {
                        this.coordinates.lat = res[0].geometry.location.lat();
                        this.coordinates.long = res[0].geometry.location.lng();
                        this.initCoordinates = true;
                        if (!isCity) {
                            this.viewport = res[0].geometry.viewport;
                        } else {
                            this.zoomLevel = 11;
                        }
                    }
                });
            }
        });
        if (this.closestStoreId) {
            this.closestMarker = this.markersMap[this.closestStoreId];
        }
    }

    applyFilter(e) {
        this.filters = e.detail && e.detail.filters;
        const stores = this.storesList.querySelectorAll('[is="m-store-locator-address"]');
        const hiddenStores = [];
        stores.forEach((store) => {
            const storeId = store.id;
            if (!this.isInFilter(store)) {
                store.classList.add('d-none');
                hiddenStores.push(storeId);
                this.toggleMarkerVisibility(storeId, false);
            } else {
                store.classList.remove('d-none');
                this.toggleMarkerVisibility(storeId, true);
            }
        });
        if (this.initMap) {
            this.resetMarkers(hiddenStores);
        }
    }

    isInFilter(store) {
        if (this.filters.length === 0) {
            return true;
        }
        const storeDepartments = store.dataset.department && store.dataset.department.split(',');
        for (const department of storeDepartments) {
            if (this.filters.indexOf(department) !== -1) {
                return true;
            }
        }
        return false;
    }

    // Get all stores to create markers
    getAllStores() {
        fetch(this.dataset.getAllStoresUrl, {
            method: 'GET',
        }).then((response) => response.json()).then((responseData) => {
            this.stores = responseData.stores;
            this.storesIds = responseData.storesIds;
            // If the map has been loaded before this call has finished, reupdate the map (create markers, update viewport ...)
            if (this.mapsLoaded) {
                this.onMapLoaded();
            }
        });
    }

    replaceUrlParams(params) {
        const { searchPageUrl } = this.dataset;
        const lastPart = searchPageUrl.split('/').pop();
        const endpoint = lastPart.split('?')[0];
        history.replaceState({}, '', `${endpoint}?${params}`);
    }

    // Called in m-stores-search when the customer select a suggestion via the storelocator:search event
    search(e) {
        let searchUrl = e.detail && e.detail.url;
        this.coordinates = {
            lat: e.detail && e.detail.params.lat,
            long: e.detail && e.detail.params.long,
        };
        this.initCoordinates = true;
        this.input = e.detail ? e.detail.params.input : 'Search';
        this.viewport = e.detail && e.detail.viewport;
        if (this.filters.length) {
            searchUrl = urlUtils.appendToUrl(searchUrl, {
                departments: this.filters.join(','),
            });
        }
        if (e.detail) {
            const params = urlUtils.getParameters(e.detail.params);
            this.replaceUrlParams(params);
        }
        fetch(searchUrl, { method: 'GET' })
            .then(handleErrors)
            .then((response) => response.json())
            .then((responseData) => {
                this.closestStoreId = responseData.closestStore;
                this.closestMarker = this.closestStoreId ? this.markersMap[this.closestStoreId] : null;
                this.storesList.classList.toggle('d-grid', responseData.stores.length > 0);
                this.storesList.innerHTML = responseData.template;
                if (this.initMap) {
                    this.closeInfoWindow();
                    this.updateMap(this.coordinates.lat, this.coordinates.long, this.viewport);
                    if (e.detail.params.country) {
                        this.initMarkersByVisibility();
                    } else {
                        this.resetMarkers([]);
                    }
                }
            });
    }

    updateMap(lat, long, viewport) {
        this.recenterMap(lat, long, viewport);
        let fitCloserMarker = false;
        this.bounds = this.map.getBounds();
        if (this.closestMarker && !this.map.getBounds().contains(this.closestMarker.position)) {
            fitCloserMarker = true;
        }

        this.fitCloserMarker = fitCloserMarker;
        if (fitCloserMarker) {
            this.bounds.extend(this.closestMarker.position);
            this.map.fitBounds(this.bounds);
        }
    }

    recenterMap(lat, long, viewport) {
        let latLng;
        if (!lat || !long || !this.initCoordinates) {
            latLng = new google.maps.LatLng(33, 3); // To center the world map. (0, 0) is a bit excentered
            this.map.setZoom(3);
            return;
        }
        latLng = new google.maps.LatLng(lat, long);
        this.map.setCenter(latLng);
        if (viewport) {
            this.map.fitBounds(viewport);
            const listener = google.maps.event.addListener(this.map, 'idle', () => {
                if (!this.fitCloserMarker) {
                    this.map.setZoom(this.map.getZoom() + 1);
                }
                google.maps.event.removeListener(listener);
            });
        } else {
            this.map.setZoom(11);
        }
        this.createSearchMarker(lat, long, this.input);
    }

    toggleView() {
        const textBtn = this.toggleViewBtn.querySelector('span');
        if (this.mapView) {
            this.storesMap.classList.add('d-none');
            this.storesList.classList.remove('d-none');
            this.storesMap.setAttribute('aria-hidden', true);
            this.storesList.setAttribute('aria-hidden', false);
            textBtn.textContent = this.toggleViewBtn.dataset.mapText;
        } else {
            this.storesMap.classList.remove('d-none');
            this.storesList.classList.add('d-none');
            this.storesMap.setAttribute('aria-hidden', false);
            this.storesList.setAttribute('aria-hidden', true);
            this.openMap(true);
            textBtn.textContent = this.toggleViewBtn.dataset.listText;
        }
        this.mapView = !this.mapView;
    }

    openMap(searchMap) {
        if (!this.initMap) {
            this.bounds = new google.maps.LatLngBounds();
            const mapOptions = {
                center: {
                    lat: this.coordinates.lat || 33,
                    lng: this.coordinates.long || 3,
                },
                restriction: {
                    // World bounds (allow overflow on east-west but not go above or below world)
                    latLngBounds: {
                        east: 180,
                        north: 85,
                        south: -85,
                        west: -180,
                    },
                    strictBounds: true,
                },
                zoom: this.zoomLevel || 3,
                minZoom: 3,
                disableDefaultUI: true,
                scaleControl: true,
                draggable: true,
                zoomControl: true,
                gestureHandling: 'cooperative',
                styles: [
                    {
                        featureType: 'poi',
                        stylers: [
                            {
                                visibility: 'off',
                            },
                        ],
                    },
                    {
                        featureType: 'administrative.country',
                        stylers: [
                            {
                                visibility: 'off',
                            },
                        ],
                    },
                ],
            };
            this.map = new google.maps.Map(this.mapCanvas, mapOptions);
            this.initMap = true;
            // Update the map when the map has been loaded
            google.maps.event.addListener(this.map, 'tilesloaded', searchMap ? this.onMapLoaded : this.onStoreMapLoaded);
        }
    }

    onStoreMapLoaded() {
        const infoWindowElt = this.querySelector('#info-window');
        const status = this.querySelector('[is="m-store-locator-title"] .status');
        const infoWindowStatus = infoWindowElt.querySelector('.status');
        if (status.classList.contains('open')) {
            infoWindowStatus.classList.add('open');
            infoWindowStatus.classList.remove('close');
        } else {
            infoWindowStatus.classList.remove('open');
            infoWindowStatus.classList.add('close');
        }

        const store = {
            name: this.querySelector('[is="m-store-locator-title"] .name').textContent,
            coordinates: this.coordinates,
            infoWindow: infoWindowElt.innerHTML,
        };
        this.createMarker(store, true);
        google.maps.event.clearListeners(this.map, 'tilesloaded');
    }

    closeInfoWindow() {
        if (this.infoWindowOpen) {
            this.infoWindowOpen.close();
        }
    }

    clusterClickHandler(event, cluster) {
        this.closeInfoWindow();
        this.map.fitBounds(cluster.bounds);
        if (this.map.zoom > 18) {
            this.map.setZoom(18);
        }
    }

    onMapLoaded() {
        if (this.stores.length) {
            const renderer = {
                render: ({ count, position }) => new google.maps.Marker({
                    position,
                    icon: {
                        url: this.svgMarker,
                        scaledSize: new google.maps.Size(33, 44),
                    },
                    label: {
                        className: 'custom-clustericon',
                        text: String(count),
                        color: 'white',
                        fontSize: '13px',
                    },
                    title: `${count} KENZO stores`,
                    zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
                }),
            };

            this.markerClusterer = new markerClusterer.MarkerClusterer({
                map: this.map,
                markers: [],
                renderer,
                onClusterClick: this.clusterClickHandler,
            });

            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get('country') === 'true') {
                this.initMarkersByVisibility();
            } else {
                this.createMarkers();
            }
            if (this.closestStoreId) {
                this.closestMarker = this.markersMap[this.closestStoreId];
            }
            this.updateMap(this.coordinates.lat, this.coordinates.long, this.viewport);
        }
        this.mapsLoaded = true;
        google.maps.event.clearListeners(this.map, 'tilesloaded');
    }

    resetMarkers(hiddenStores) {
        this.removeMarkers();
        this.storesHidden = hiddenStores;
        this.createMarkers();
    }

    removeMarkers() {
        if (this.markerClusterer) {
            this.markerClusterer.clearMarkers();
        }
        this.markers.forEach((marker) => {
            marker.setMap(null);
        });
        this.markers = [];
        this.markersMap = {};
    }

    createMarker(store, openInfoWindow) {
        const infoWindow = new google.maps.InfoWindow({
            content: store.infoWindow,
            ariaLabel: store.name,
        });
        const icon = {
            url: this.svgMarkerBrand,
            scaledSize: { width: 45, height: 60 },
        };
        const marker = new google.maps.Marker({
            position: { lat: store.coordinates.lat, lng: store.coordinates.long },
            title: store.name,
            icon,
        });
        marker.addListener('click', () => {
            if (this.infoWindowOpen) {
                this.infoWindowOpen.close();
            }
            infoWindow.open({
                anchor: marker,
                map: this.map,
            });
            this.infoWindowOpen = infoWindow;
        });
        if (openInfoWindow) {
            infoWindow.open({
                anchor: marker,
                map: this.map,
            });
        }
        marker.setMap(this.map);
        return marker;
    }

    createMarkers(fitCloserMarker) {
        this.stores.forEach((store) => {
            const marker = this.createMarker(store, false);
            // Extend the map to the closest store if no stores are visible in the current viewport
            if (fitCloserMarker && store.id === this.closestStoreId) {
                this.bounds.extend(marker.position);
                this.map.fitBounds(this.bounds);
            }
            if (this.storesHidden.includes(store.id)) {
                marker.setVisible(false);
            } else {
                this.markerClusterer.addMarker(marker);
            }
            this.markers.push(marker);
            this.markersMap[store.id] = marker;
        });
    }

    createSearchMarker(lat, long, input) {
        if (this.searchMarker) {
            this.searchMarker.setMap(null);
        }
        const icon = {
            url: this.svgMarkerSearch,
            scaledSize: { width: 10, height: 10 },
        };
        this.searchMarker = new google.maps.Marker({
            position: { lat, lng: long },
            title: input,
            icon,
            zIndex: 0,
        });
        this.searchMarker.setMap(this.map);
    }

    initMarkersByVisibility() {
        const stores = this.querySelectorAll('[is="m-store-locator-address"]');
        const visibleStoresIds = [];
        for (const store of stores) {
            visibleStoresIds.push(store.id);
        }
        const hiddenStores = [];
        for (const markerId of this.storesIds) {
            if (!visibleStoresIds.includes(markerId)) {
                hiddenStores.push(markerId);
            }
        }
        this.resetMarkers(hiddenStores);
    }

    toggleMarkerVisibility(id, value) {
        const marker = this.markersMap[id];
        if (marker) {
            marker.setVisible(value);
        }
    }

    triggerStoreEvent(e) {
        const dataContainer = e.target.closest('[data-store-gtm-data]');
        const data = JSON.parse(dataContainer.dataset.storeGtmData);
        const eventName = 'store_locator_store';
        const elemEventParent = e.target.parentElement;
        data.store_name = elemEventParent.title;
        if (data) {
            document.dispatchEvent(new CustomEvent('gtm:pushEvent', {
                detail: {
                    data,
                    eventName,
                    pageType: true,
                    getListInfo: true,
                    target: e.target,
                },
            }));
        }
    }
}

customElements.define('o-store-locator', oStoreLocator, {
    extends: 'div',
});
